// @flow
import React, { useRef } from 'react';
import dynamic from 'next/dynamic';

import config from 'Config';
import {
  GOOGLE_API_STREET_VIEW_PATH,
  GOOGLE_API_KEY,
} from 'Components/utils/google/google.constants';
import { getStaticMapURL } from 'Components/utils/google/google.utils';

import { Map } from 'Components/frameworks/map/Map.jsx';

const GoogleMapReact = dynamic(() => import('google-map-react'), { ssr: false });

type GoogleStreetViewProps = {
  className?: string,
  closeLabel?: string,
  fov?: number,
  heading?: number,
  height?: number,
  latitude: number,
  longitude: number,
  title?: string,
  width?: number,
};

const addStreetView = ({ map, maps, position, pov, ref }) => {
  const panorama = new maps.StreetViewPanorama(ref, {
    fullscreenControl: false,
    position,
    pov,
    linksControl: true,
    panControl: true,
  });

  map.setStreetView(panorama);
};

export function GoogleStreetView(props: GoogleStreetViewProps) {
  const {
    className,
    closeLabel,
    fov = 2,
    heading = 2,
    height = 120,
    latitude,
    longitude,
    title,
    width = 640,
  } = props;

  const coordinates = { lat: latitude, lng: longitude };
  const staticMapOptions = {
    size: `${width}x${height}`,
    location: `${latitude},${longitude}`,
    fov,
    heading,
    pitch: 0,
    // guess what, the pitch that gets returned from the API is all kinds of wrong
    // because it really likes looking at ceilings; people don't want to look at
    // ceilings, so we hardcode the pitch to 0 until the API gets its act together
    // and returns 0; tbh we shouldn't even need the API to provide this to us.
  };

  const _map: { current: ?HTMLDivElement } = useRef();

  return (
    <Map
      className={className}
      closeLabel={closeLabel}
      dataTestId="streetview-preview"
      imageSrc={getStaticMapURL(GOOGLE_API_STREET_VIEW_PATH, staticMapOptions)}
      ratio={{
        w: width,
        h: height,
      }}
      title={title}
      trackingData={{
        categoryGA: 'resort',
        actionGA: 'mapClick',
        labelGA: 'GoogleStreetView',
      }}
    >
      <div className="h-full" ref={_map}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: config.get(GOOGLE_API_KEY) }}
          defaultCenter={coordinates}
          defaultZoom={fov}
          onGoogleApiLoaded={({ map, maps }) =>
            addStreetView({
              map,
              maps,
              position: coordinates,
              pov: { heading, pitch: 0 },
              // same here, for now
              ref: _map.current,
            })
          }
          yesIWantToUseGoogleMapApiInternals
        />
      </div>
    </Map>
  );
}
